import { AbstractUser, ArticleType, mapToCard } from "@kvix/shared";
import {
  CardList,
  KvixIcon,
  SessionSwiper,
  SessionSwiperItem,
  Text,
} from "@kvix/ui";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  makeStyles,
} from "@material-ui/core";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { isBasicInstructor, KvixUserContext } from "../../../../contexts/user";
import {
  useFetchFavouritedInstructors,
  useFetchFavouritedPrograms,
  useFetchFavouritedSessions,
} from "../../../../hooks/http";
import { useFetchFavouritedPlaylists } from "../../../../hooks/playlist";
import { Card } from "../../../partials/Content/Card";
import { ProfileSection } from "../partials/Section";

const useStyles = makeStyles((theme) => ({
  favContainer: {
    display: "flex",
    justifyContent: "space-between",
  },
  expandIcon: {
    "& .MuiSvgIcon-root": {
      color: theme.palette.primary.main,
    },
  },
  slideContainer: {
    "& .MuiPaper-root": {
      backgroundColor: theme.palette.background.default,
    },
    "& .MuiAccordionSummary-root": {
      padding: "0px",
    },
    "& .MuiAccordionDetails-root": {
      padding: "8px 0 16px",
      display: "block",
    },
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: theme.typography.fontWeightRegular,
  },
}));

export const ProfilePageFavourites: React.FC = (props) => {
  const context = useContext(KvixUserContext);
  const classes = useStyles(props);
  const {
    favouritedProgramIds,
    favouritedSessionIds,
    favouritedPlaylistIds,
    actions,
    user,
    followedInstructors,
  } = context;
  const { t, ready } = useTranslation("account");
  const favouritedPrograms = useFetchFavouritedPrograms(favouritedProgramIds);
  const favouritedPlaylists = useFetchFavouritedPlaylists(
    favouritedPlaylistIds
  );
  const favouritedSessions = useFetchFavouritedSessions(favouritedSessionIds);
  const favouritedInstructors =
    useFetchFavouritedInstructors(followedInstructors);
  const [displayPrograms, setDisplayPrograms] = useState(true);
  const [displayPlaylists, setDisplayPlaylists] = useState(true);
  const [displaySessions, setDisplaySessions] = useState(true);
  const [displayInstructors, setDisplayInstructors] = useState(true);
  const isFollowing = (instructor: AbstractUser) => {
    if (instructor?.instructorProfile && followedInstructors) {
      return followedInstructors.hasOwnProperty(
        instructor.instructorProfile.id
      );
    }
    return null;
  };

  if (!ready) {
    return null;
  }

  return (
    <ProfileSection
      title={<Text variant="h4">{t("favourites.myFavourites.title")}</Text>}
      tab="favorites"
    >
      {favouritedPrograms.length > 0 ||
      favouritedSessions.length > 0 ||
      favouritedInstructors.length > 0 ||
      favouritedPlaylists.length > 0 ? (
        <>
          <div className={classes.slideContainer}>
            <Accordion expanded={displayPrograms}>
              <AccordionSummary
                className={classes.expandIcon}
                expandIcon={<KvixIcon.Expand.ExpandMore />}
                onClick={() => setDisplayPrograms(!displayPrograms)}
                aria-controls="panel1a-content"
              >
                <div className={classes.heading}>
                  <Text variant="h5">{`${t("favourites.programs")} (${
                    favouritedPrograms.length
                  })`}</Text>
                </div>
              </AccordionSummary>
              <AccordionDetails>
                {favouritedPrograms.length > 0 ? (
                  <CardList noArrows>
                    {favouritedPrograms.map((program) => (
                      <Card
                        type={ArticleType.PROGRAM}
                        options={mapToCard({ program })}
                        key={program.id}
                        toggleFavourite={() =>
                          actions.toggleFavourite({ programId: program.id })
                        }
                      />
                    ))}
                  </CardList>
                ) : (
                  <Text>{t("favourites.noFavourites")}</Text>
                )}
              </AccordionDetails>
            </Accordion>
          </div>
          <div className={classes.slideContainer}>
            <Accordion expanded={displaySessions}>
              <AccordionSummary
                className={classes.expandIcon}
                onClick={() => setDisplaySessions(!displaySessions)}
                expandIcon={<KvixIcon.Expand.ExpandMore />}
                aria-controls="panel1a-content"
              >
                <div className={classes.heading}>
                  <Text variant="h5">{`${t("favourites.sessions")} (${
                    favouritedSessions.length
                  })`}</Text>
                </div>
              </AccordionSummary>
              <AccordionDetails>
                {favouritedSessions.length > 0 ? (
                  <SessionSwiper
                    noArrows
                    sessions={favouritedSessions}
                    user={user}
                    favouritedSessionIds={favouritedSessionIds}
                    toggleFavourite={actions.toggleFavourite}
                  >
                    {(itemProps) => <SessionSwiperItem {...itemProps} />}
                  </SessionSwiper>
                ) : (
                  <Text>{t("favourites.noFavourites")}</Text>
                )}
              </AccordionDetails>
            </Accordion>
          </div>
          <div className={classes.slideContainer}>
            <Accordion expanded={displayInstructors}>
              <AccordionSummary
                className={classes.expandIcon}
                expandIcon={<KvixIcon.Expand.ExpandMore />}
                onClick={() => setDisplayInstructors(!displayInstructors)}
                aria-controls="panel1a-content"
              >
                <div className={classes.heading}>
                  <Text variant="h5">{`${t("favourites.instructors")} (${
                    favouritedInstructors.length
                  })`}</Text>
                </div>
              </AccordionSummary>
              <AccordionDetails>
                {!!favouritedInstructors.length ? (
                  <CardList noArrows>
                    {favouritedInstructors?.map((instructor) => {
                      return (
                        <Card
                          type={
                            isBasicInstructor(instructor)
                              ? ArticleType.BASIC_INSTRUCTOR
                              : ArticleType.INSTRUCTOR
                          }
                          options={mapToCard({
                            instructor,
                          })}
                          key={instructor.id}
                          toggleFavourite={
                            isFollowing(instructor)
                              ? () =>
                                  actions.removeInstructorFollow(
                                    instructor.instructorProfile.id
                                  )
                              : () =>
                                  actions.updateInstructorFollow(
                                    instructor.instructorProfile.id,
                                    true
                                  )
                          }
                        />
                      );
                    })}
                  </CardList>
                ) : (
                  <Text>{t("favourites.noFavourites")}</Text>
                )}
              </AccordionDetails>
            </Accordion>
          </div>
          <div className={classes.slideContainer}>
            <Accordion expanded={displayPlaylists}>
              <AccordionSummary
                className={classes.expandIcon}
                expandIcon={<KvixIcon.Expand.ExpandMore />}
                onClick={() => setDisplayPlaylists(!displayPlaylists)}
                aria-controls="panel1a-content"
              >
                <div className={classes.heading}>
                  <Text variant="h5">{`${t("favourites.playlists")} (${
                    favouritedPlaylists.length
                  })`}</Text>
                </div>
              </AccordionSummary>
              <AccordionDetails>
                {favouritedPlaylists.length > 0 ? (
                  <CardList noArrows>
                    {favouritedPlaylists.map((playlist) => (
                      <Card
                        type={ArticleType.PLAYLIST}
                        options={mapToCard({ playlist })}
                        key={playlist.id}
                        toggleFavourite={() =>
                          actions.toggleFavourite({ playlistId: playlist.id })
                        }
                      />
                    ))}
                  </CardList>
                ) : (
                  <Text>{t("favourites.noFavourites")}</Text>
                )}
              </AccordionDetails>
            </Accordion>
          </div>
        </>
      ) : (
        <Text>{t("favourites.noFavouritesFullText")}</Text>
      )}
    </ProfileSection>
  );
};
